import { useState, useEffect, useCallback } from "react";
import { formatPlAmount, formatPlChangePercent } from "../utils/formatPlAmount";
import type { MtdMetricCardProps } from "../components/client_overview_components/MtdMetricCard";
import {
  getClientOverviewBootstrapData,
  getClientOverviewBootstrapError,
  getClientOverviewBootstrapPromise,
} from "../state/clientOverviewBootstrapStore";
import { useClientOverviewBootstrap } from "./useClientOverviewBootstrap";

export type MtdMetricCardData = MtdMetricCardProps;

export interface WeekSummaryBulletData {
  variant: "error" | "warning" | "neutral" | "success";
  text: string;
}

export interface ThisMonthInProgressData {
  monthTitle: string;
  /** Effective reporting date — MTD panel header (e.g. as of March 31, 2025) */
  asOfLabel: string;
  /** Snapshot date for cash / AR / AP — balances panel header */
  balancesAsOfLabel: string;
  mtdCardsFirstRow: MtdMetricCardData[];
  mtdCardsSecondRow: MtdMetricCardData[];
}

/** Revenue: green when >= 0, red when negative (same as API delta vs last). */
function changeVariant(percent: number | null): "success" | "error" {
  if (percent === null || percent === undefined) return "success";
  return percent >= 0 ? "success" : "error";
}

/** Expense MTD: API sends raw delta (positive = spend up vs last). Red when spend rose, green when it fell. */
function expenseMtdChangeVariant(percent: number | null): "success" | "error" {
  if (percent === null || percent === undefined) return "success";
  return percent >= 0 ? "error" : "success";
}

function trendIcon(
  percent: number | null | undefined,
): "trendingUp" | "trendingDown" {
  if (percent === null || percent === undefined) return "trendingUp";
  return percent < 0 ? "trendingDown" : "trendingUp";
}

const DEFAULT_FIRST_ROW: MtdMetricCardData[] = [
  {
    label: "Revenue MTD",
    value: "—",
    changeText: "—",
    changeVariant: "success",
    subtext: "vs same period last month",
    icon: "trendingUp",
  },
  {
    label: "Expenses MTD",
    value: "—",
    changeText: "—",
    changeVariant: "success",
    subtext: "vs same period last month",
    icon: "trendingDown",
  },
];

const DEFAULT_SECOND_ROW: MtdMetricCardData[] = [
  {
    label: "Cash Balance",
    value: "—",
    scopeFooterText: "across all bank accounts",
    icon: "dollarSign",
  },
  {
    label: "Receivables",
    value: "—",
    scopeFooterText: "across all invoices",
    icon: "fileText",
  },
  {
    label: "Payables",
    value: "—",
    scopeFooterText: "across all bills",
    icon: "creditCard",
  },
];

export function useThisMonthInProgress(): ThisMonthInProgressData & {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  useClientOverviewBootstrap();

  const [monthTitle, setMonthTitle] = useState("");
  const [asOfLabel, setAsOfLabel] = useState("");
  const [balancesAsOfLabel, setBalancesAsOfLabel] = useState("");
  const [mtdCardsFirstRow, setMtdCardsFirstRow] =
    useState<MtdMetricCardData[]>(DEFAULT_FIRST_ROW);
  const [mtdCardsSecondRow, setMtdCardsSecondRow] = useState<
    MtdMetricCardData[]
  >(DEFAULT_SECOND_ROW);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThisMonthInProgress = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let bootstrapEntity = getClientOverviewBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getClientOverviewBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getClientOverviewBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(
          bootstrapError ?? "Overview bootstrap data unavailable",
        );
      }

      const mtdSection = bootstrapEntity.sections.thisMonthInProgress;

      if (mtdSection.status !== "ok" || !mtdSection.data) {
        throw new Error(
          mtdSection.error ?? "This month in progress unavailable",
        );
      }

      const mtdEntity = mtdSection.data;

      setMonthTitle(mtdEntity.monthTitle);
      setAsOfLabel(mtdEntity.asOfLabel);
      setBalancesAsOfLabel(mtdEntity.balancesAsOfLabel ?? "");
      setMtdCardsFirstRow([
        {
          label: "Revenue MTD",
          value: formatPlAmount(mtdEntity.revenueMtd),
          changeText: formatPlChangePercent(mtdEntity.revenueMtdChangePercent),
          changeVariant: changeVariant(mtdEntity.revenueMtdChangePercent),
          subtext: "vs same period last month",
          icon: trendIcon(mtdEntity.revenueMtdChangePercent),
        },
        {
          label: "Expenses MTD",
          value: formatPlAmount(mtdEntity.expenseMtd),
          changeText: formatPlChangePercent(mtdEntity.expenseMtdChangePercent),
          changeVariant: expenseMtdChangeVariant(mtdEntity.expenseMtdChangePercent),
          subtext: "vs same period last month",
          icon: trendIcon(mtdEntity.expenseMtdChangePercent),
        },
      ]);
      setMtdCardsSecondRow([
        {
          label: "Cash Balance",
          value: formatPlAmount(mtdEntity.cashBalanceMtd),
          scopeFooterText: "across all bank accounts",
          icon: "dollarSign",
        },
        {
          label: "Receivables",
          value: formatPlAmount(mtdEntity.receivablesMtd),
          scopeFooterText: "across all invoices",
          icon: "fileText",
        },
        {
          label: "Payables",
          value: formatPlAmount(mtdEntity.payablesMtd),
          scopeFooterText: "across all bills",
          icon: "creditCard",
        },
      ]);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load this month in progress";
      setError(message);
      setMtdCardsFirstRow(DEFAULT_FIRST_ROW);
      setMtdCardsSecondRow(DEFAULT_SECOND_ROW);
      setMonthTitle("");
      setAsOfLabel("");
      setBalancesAsOfLabel("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchThisMonthInProgress();
  }, [fetchThisMonthInProgress]);

  return {
    monthTitle: monthTitle || "—",
    asOfLabel: asOfLabel || "—",
    balancesAsOfLabel: balancesAsOfLabel || "—",
    mtdCardsFirstRow,
    mtdCardsSecondRow,
    loading,
    error,
    refetch: fetchThisMonthInProgress,
  };
}
